import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { apiSearchProjectDocs } from '../../api/search';
import { buildCheckMenuItems } from '../../helpers/contextMenuHelpers';
import InlineEditor from '../ProjectPanel/InlineEditor';
import AppInput from '../Inputs/AppInput';
import ContextMenu from './ContextMenu';
import Popover from './Popover';
import ModalTabShelf from './ModalTabShelf';
import Icon from '../Icon';

const parseProjectDocId = (href) => {
  const match = String(href || '').trim().match(/^ghost:\/\/doc\/([^/?#]+)/i);
  return match?.[1] || null;
};

const PROJECT_TARGETS = new Set(['this-tab', 'new-tab', 'new-tab-opposite']);

const parseProjectLaunchTarget = (href) => {
  const raw = String(href || '').trim();
  const match = raw.match(/[?&]target=([^&#]+)/i);
  const decoded = match?.[1] ? decodeURIComponent(match[1]).toLowerCase() : '';
  return PROJECT_TARGETS.has(decoded) ? decoded : 'this-tab';
};

const buildProjectHref = (docId, launchTarget = 'this-tab') => {
  const normalizedId = String(docId || '').trim();
  if (!normalizedId) return '';
  const normalizedTarget = PROJECT_TARGETS.has(launchTarget) ? launchTarget : 'this-tab';
  if (normalizedTarget === 'this-tab') return `ghost://doc/${normalizedId}`;
  return `ghost://doc/${normalizedId}?target=${encodeURIComponent(normalizedTarget)}`;
};

const parseExternalLaunchTarget = (target) => {
  return String(target || '').toLowerCase() === '_blank' ? 'new-browser-window' : 'new-browser-window';
};

export default function PopoverLinks({ editor, url, linkTarget = null, defaultMode = null, position = {x:0,y:0}, onClose, editing: initialEditing = false, onEditingChange = null, projectId = null, itemsMap = null, onOpenProjectDoc = null, canEdit = true, anchorPos = null, triggerEl = null }) {
  const [editing, setEditing] = useState(!!initialEditing && !!canEdit);
  const [value, setValue] = useState(url || '');
  const initialProjectDocId = parseProjectDocId(url);
  const [mode, setMode] = useState(() => {
    if (initialProjectDocId) return 'project';
    if (String(defaultMode || '').toLowerCase() === 'project') return 'project';
    return 'external';
  });
  const [externalValue, setExternalValue] = useState(initialProjectDocId ? '' : (url || ''));
  const [projectQuery, setProjectQuery] = useState('');
  const [projectResults, setProjectResults] = useState([]);
  const [projectSearchBusy, setProjectSearchBusy] = useState(false);
  const [selectedProjectDocId, setSelectedProjectDocId] = useState(initialProjectDocId || null);
  const [selectedProjectDocLabel, setSelectedProjectDocLabel] = useState('');
  const suppressNextProjectSearchRef = useRef(false);

  const getDocName = useCallback((docId) => {
    if (!docId || !itemsMap) return '';
    try {
      if (itemsMap instanceof Map) {
        return (
          itemsMap.get(`doc:${docId}`)?.name ||
          itemsMap.get(String(docId))?.name ||
          itemsMap.get(Number(docId))?.name ||
          ''
        );
      }
      return (
        itemsMap[`doc:${docId}`]?.name ||
        itemsMap[String(docId)]?.name ||
        itemsMap[Number(docId)]?.name ||
        ''
      );
    } catch (err) {
      return '';
    }
  }, [itemsMap]);

  const getNormalizedResultName = useCallback((item, id) => {
    const payloadName = item?.name || item?.doc_name || item?.docName || item?.title || item?.doc?.name || '';
    const mappedName = getDocName(String(id));
    return String(payloadName || mappedName || `Document ${id}`);
  }, [getDocName]);

  useEffect(() => setValue(url || ''), [url]);
  useEffect(() => { setEditing(!!initialEditing && !!canEdit); }, [initialEditing, canEdit]);
  useEffect(() => {
    if (!canEdit && editing) setEditing(false);
  }, [canEdit, editing]);
  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);
  useEffect(() => {
    const nextProjectDocId = parseProjectDocId(url);
    const nextProjectDocLabel = nextProjectDocId ? (getDocName(nextProjectDocId) || `Document ${nextProjectDocId}`) : '';
    const nextProjectTarget = nextProjectDocId ? parseProjectLaunchTarget(url) : 'this-tab';
    const nextExternalTarget = parseExternalLaunchTarget(linkTarget);
    const nextMode = nextProjectDocId
      ? 'project'
      : (String(defaultMode || '').toLowerCase() === 'project' ? 'project' : 'external');
    setMode(nextMode);
    setExternalValue(nextProjectDocId ? '' : (url || ''));
    setExternalLaunchTarget(nextExternalTarget);
    setSelectedProjectDocId(nextProjectDocId || null);
    setSelectedProjectDocLabel(nextProjectDocLabel);
    setProjectLaunchTarget(nextProjectTarget);
    setProjectQuery('');
    setProjectResults([]);
  }, [url, linkTarget, defaultMode, getDocName]);

  const [externalLaunchTarget, setExternalLaunchTarget] = useState('new-browser-window');
  const [projectLaunchTarget, setProjectLaunchTarget] = useState('this-tab');
  const caretRef = useRef(null);
  const [showLaunchMenu, setShowLaunchMenu] = useState(false);
  const triggerRef = useMemo(() => ({ current: triggerEl || null }), [triggerEl]);
  const popoverInnerRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (mode !== 'project') {
      setProjectResults([]);
      setProjectSearchBusy(false);
      return;
    }

    if (suppressNextProjectSearchRef.current) {
      suppressNextProjectSearchRef.current = false;
      setProjectResults([]);
      setProjectSearchBusy(false);
      return;
    }

    const query = String(projectQuery || '').trim();
    if (!projectId || query.length < 3) {
      setProjectResults([]);
      setProjectSearchBusy(false);
      return;
    }

    let cancelled = false;
    setProjectSearchBusy(true);

    apiSearchProjectDocs(projectId, query, 5, {
      style: 'SEARCH_UI',
      resultType: 'docs',
      rankMode: 'max',
      frontendFilter: {
        nameOnly: true,
        nameMatch: 'contains'
      },
      weights: {
        NAME_EXACT: 120,
        NAME_PREFIX: 90,
        NAME_TRGM: 45,
        MD_EXACT: 0,
        MD_TRGM: 0,
        MD_FTS: 0,
        HEADER_HIT: 0
      }
    })
      .then((results) => {
        if (cancelled) return;
        const normalized = [];
        const seen = new Set();

        for (const item of (Array.isArray(results) ? results : [])) {
          const id = item?.id ?? item?.docId ?? item?.doc_id ?? item?.doc?.id ?? null;
          if (id === null || id === undefined) continue;
          const key = String(id);
          if (seen.has(key)) continue;
          seen.add(key);
          normalized.push({
            id: key,
            name: getNormalizedResultName(item, id)
          });
          if (normalized.length >= 5) break;
        }

        setProjectResults(normalized);
      })
      .catch(() => {
        if (!cancelled) setProjectResults([]);
      })
      .finally(() => {
        if (!cancelled) setProjectSearchBusy(false);
      });

    return () => { cancelled = true; };
  }, [mode, projectId, projectQuery, getNormalizedResultName]);

  useEffect(() => {
    if (!selectedProjectDocId) return;
    const resolved = getDocName(selectedProjectDocId);
    if (!resolved) return;
    if (!selectedProjectDocLabel || /^Document\s+/i.test(selectedProjectDocLabel)) {
      setSelectedProjectDocLabel(resolved);
    }
  }, [selectedProjectDocId, selectedProjectDocLabel, getDocName]);

  const savedProjectDocId = parseProjectDocId(value || url || '');
  const activeProjectDocId = editing ? selectedProjectDocId : (selectedProjectDocId || savedProjectDocId);

  const saveEdit = async () => {
    if (!canEdit) {
      setEditing(false);
      onClose?.();
      return;
    }
    const nextExternalHref = String(externalValue || '').trim();
    const nextProjectDocId = selectedProjectDocId || null;
    const nextProjectDocLabel = nextProjectDocId
      ? (selectedProjectDocLabel || getDocName(nextProjectDocId) || `Document ${nextProjectDocId}`)
      : '';
    const newHref = mode === 'project' ? buildProjectHref(nextProjectDocId, projectLaunchTarget) : nextExternalHref;
    const linkAttrs = mode === 'external'
      ? (externalLaunchTarget === 'new-browser-window'
        ? { href: newHref, target: '_blank', rel: 'noopener noreferrer' }
        : { href: newHref, target: null, rel: null })
      : { href: newHref, target: null, rel: null };
    // If no href provided, do not apply a link
    if (!newHref) {
      setEditing(false);
      return onClose?.();
    }
    setValue(newHref);
    if (mode === 'external') setExternalValue(newHref);
    if (mode === 'project' && nextProjectDocId) {
      setSelectedProjectDocLabel(nextProjectDocLabel);
    }
    if (!editor) return onClose?.();
    try {
      const state = editor.view && editor.view.state;
      const sel = state ? state.selection : null;
      const isEmpty = sel ? sel.empty : true;
      let appliedToExisting = false;

      const applyToExistingSelection = () => {
        if (!editor?.chain) return false;
        try {
          const chain = editor.chain().focus();
          if (Number.isInteger(anchorPos)) {
            chain.setTextSelection(anchorPos);
          }
          chain.extendMarkRange('link').setLink(linkAttrs);
          return !!chain.run();
        } catch (err) {
          return false;
        }
      };

      appliedToExisting = applyToExistingSelection();

      if (!appliedToExisting && isEmpty) {
        if (mode === 'external') {
          const from = sel ? sel.from : 0;
          try {
            editor.chain().focus().insertContentAt(from, newHref).setTextSelection({ from, to: from + newHref.length }).extendMarkRange('link').setLink(linkAttrs).run();
          } catch (err) {
            try { editor.chain().focus().updateAttributes('link', linkAttrs).run(); } catch (e) {}
          }
        } else {
          try { editor.chain().focus().updateAttributes('link', linkAttrs).run(); } catch (e) {}
        }
      } else if (!appliedToExisting) {
        if (editor.chain) {
          try {
            editor.chain().focus().extendMarkRange('link').setLink(linkAttrs).run();
          } catch (err) {
            try { editor.chain().focus().updateAttributes('link', linkAttrs).run(); } catch (e) {}
          }
        }
      }
    } catch (err) {}
    setEditing(false);
    onClose?.();
  };

  const launchExternal = (target) => {
    const targetUrl = value || url || '';
    if (!targetUrl) return;
    if (target === 'new-browser-window') {
      window.open(targetUrl, '_blank', 'noopener');
      onClose?.();
      return;
    }
    window.location.href = targetUrl;
  };

  const launchProject = (target) => {
    const docId = activeProjectDocId;
    if (!docId) return;
    onOpenProjectDoc?.(docId, target);
    onClose?.();
  };

  const launchOptions = mode === 'project'
    ? [
      { value: 'this-tab', label: 'This tab' },
      { value: 'new-tab', label: 'New Tab' },
      { value: 'new-tab-opposite', label: 'New Tab Opposite' },
    ]
    : [
      { value: 'this-browser-window', label: 'This Browser Window' },
      { value: 'new-browser-window', label: 'New Browser Window' },
    ];

  const launchItems = buildCheckMenuItems({
    options: launchOptions,
    selectedValue: mode === 'project' ? projectLaunchTarget : externalLaunchTarget,
    onSelect: (value) => {
      if (mode === 'project') setProjectLaunchTarget(value);
      else setExternalLaunchTarget(value);
    },
    onAfterSelect: () => setShowLaunchMenu(false),
  });

  const openCurrent = () => {
    const currentHref = String(value || url || '').trim();
    const currentDocId = parseProjectDocId(currentHref);
    if (mode === 'project' || currentDocId) {
      const launchTarget = currentDocId ? parseProjectLaunchTarget(currentHref) : projectLaunchTarget;
      launchProject(launchTarget);
      return;
    }
    launchExternal(externalLaunchTarget);
  };

  const removeCurrentLink = () => {
    if (!canEdit) return;
    if (!editor?.chain) {
      onClose?.();
      return;
    }
    try {
      const chain = editor.chain().focus();
      if (Number.isInteger(anchorPos)) {
        chain.setTextSelection(anchorPos);
      }
      chain.extendMarkRange('link').unsetLink().run();
    } catch (err) {}
    onClose?.();
  };

  const currentHref = String(value || url || '').trim();
  const currentHrefProjectDocId = parseProjectDocId(currentHref);
  const currentHrefProjectDocName = getDocName(currentHrefProjectDocId);
  const currentHrefIsSelectedProjectDoc =
    currentHrefProjectDocId && selectedProjectDocId && String(currentHrefProjectDocId) === String(selectedProjectDocId);
  const displayLabel = currentHrefProjectDocId
    ? (currentHrefProjectDocName || (currentHrefIsSelectedProjectDoc ? selectedProjectDocLabel : '') || `Document ${currentHrefProjectDocId}`)
    : (currentHref || '—');
  const selectedLaunchLabel = mode === 'project'
    ? (projectLaunchTarget === 'this-tab' ? 'This tab' : projectLaunchTarget === 'new-tab' ? 'New Tab' : 'New Tab Opposite')
    : (externalLaunchTarget === 'this-browser-window' ? 'This Browser Window' : 'New Browser Window');

  const modeTabs = [
    { key: 'project', label: 'This Project' },
    { key: 'external', label: 'External URL' }
  ];

  useEffect(() => {
    // Auto-close behavior: when not editing, close popover when the mouse is
    // neither over the popover nor over an <a> whose href matches the current link.
    if (editing) return;

    const clearTimer = () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };

    const startCloseTimer = (delay = 120) => {
      clearTimer();
      closeTimerRef.current = setTimeout(() => {
        onClose?.();
      }, delay);
    };

    const onMove = (e) => {
      try {
        const el = e.target;
        if (!el) return startCloseTimer();
        // If cursor is over the popover, keep open
        if (popoverInnerRef.current && popoverInnerRef.current.contains(el)) {
          return clearTimer();
        }
        // If cursor is over an anchor with matching href, keep open
        const a = el.closest && el.closest('a');
        if (a) {
          const ah = String(a.getAttribute('href') || a.href || '').trim();
          if (ah && ah === currentHref) return clearTimer();
        }
        // otherwise schedule close
        startCloseTimer();
      } catch (err) {}
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onMove);
    window.addEventListener('scroll', onMove, true);

    // Ensure we clean up timer/listeners
    return () => {
      clearTimer();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [editing, currentHref, onClose]);

  return (
    <Popover
      isOpen={true}
      onClose={onClose}
      x={position?.x}
      y={position?.y}
      className="linkPopover"
      triggerRef={triggerRef?.current ? triggerRef : null}
      placement="bottom"
    >
      {!editing ? (
        <div
          ref={popoverInnerRef}
          className="linkDisplayRow"
          onMouseEnter={() => { if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; } }}
          onMouseLeave={() => { if (!editing) { closeTimerRef.current = setTimeout(() => onClose?.(), 120); } }}
        >
          <a
            href={String(value || url || '').trim() || '#'}
            className="linkUrl linkUrlAnchor"
            title={displayLabel}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openCurrent();
            }}
          >
            {displayLabel}
          </a>
          <div className="linkMenuActions">
            {canEdit && (
              <button className="iconButton" aria-label="Edit" onClick={() => setEditing(true)}>
                <Icon icon={'pencil-simple'} className="icon pencil-simple" />
              </button>
            )}
            {canEdit && (
              <button className="iconButton" aria-label="Remove Link" onClick={removeCurrentLink}>
                <Icon icon={'link-break'} className="icon link-break" />
              </button>
            )}
            <button className="iconButton" aria-label="Go" onClick={openCurrent}>
              <Icon icon={'arrow-square-out'} className="icon arrow-square-out" />
            </button>
          </div>
        </div>
      ) : (
        <div>
            <ModalTabShelf
              tabs={modeTabs}
              activeKey={mode}
              onChange={(nextMode) => {
                setMode(nextMode);
                if (nextMode === 'project') {
                  const currentProjectDocId = selectedProjectDocId || parseProjectDocId(value || url || '');
                  const currentProjectDocLabel = currentProjectDocId
                    ? (selectedProjectDocLabel || getDocName(currentProjectDocId) || `Document ${currentProjectDocId}`)
                    : '';
                  setSelectedProjectDocId(currentProjectDocId || null);
                  setSelectedProjectDocLabel(currentProjectDocLabel);
                  setProjectQuery('');
                  setProjectResults([]);
                }
                setShowLaunchMenu(false);
              }}
              className="modalTabShelf"
            />
          <div className="linkPopoverBody">
            {mode === 'external' ? (
              <InlineEditor
                value={externalValue}
                inputClassName="linkInput"
                onChange={(nextValue) => {
                  setExternalValue(nextValue);
                  if (String(nextValue || '').trim()) {
                    setSelectedProjectDocId(null);
                    setSelectedProjectDocLabel('');
                    setProjectQuery('');
                    setProjectResults([]);
                  }
                }}
                onSave={saveEdit}
                onCancel={() => setEditing(false)}
                placeholder="Target URL"
                saveOnBlur={false}
              />
            ) : (
              <div>
                {selectedProjectDocId ? (
                  <div
                    className="contextItem linkProjectSelection"
                  >
                    <span className="linkProjectSelectionLabel">
                      {selectedProjectDocLabel || getDocName(selectedProjectDocId) || `Document ${selectedProjectDocId}`}
                    </span>
                    <button
                      type="button"
                      className="iconButton small"
                      aria-label="Remove selected doc"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedProjectDocId(null);
                        setSelectedProjectDocLabel('');
                        setProjectQuery('');
                        setProjectResults([]);
                        setProjectSearchBusy(false);
                        suppressNextProjectSearchRef.current = false;
                      }}
                    >
                      <Icon icon={'x'} className="icon small x" />
                    </button>
                  </div>
                ) : (
                  <>
                    <AppInput
                      type="text"
                      className="linkInput linkProjectSearchInput"
                      value={projectQuery}
                      onChange={(e) => {
                        const nextQuery = e.target.value;
                        setProjectQuery(nextQuery);
                        setExternalValue('');
                        setSelectedProjectDocId(null);
                        setSelectedProjectDocLabel('');
                      }}
                      placeholder="Search project docs"
                      autoComplete="off"
                    />
                    <div className="linkProjectResults">
                      {projectQuery.trim().length < 3 ? (
                        null
                      ) : projectSearchBusy ? (
                        <div className="hint">Searching…</div>
                      ) : projectResults.length === 0 ? (
                        <div className="hint">No docs found.</div>
                      ) : (
                        projectResults.map(doc => (
                          <div
                            key={doc.id}
                            className="searchResultItem linkProjectResultItem"
                            role="button"
                            tabIndex={0}
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const nextId = String(doc.id);
                              const nextLabel = String(doc.name || `Document ${nextId}`);
                              setSelectedProjectDocId(nextId);
                              setSelectedProjectDocLabel(nextLabel);
                              setProjectQuery('');
                              setProjectResults([]);
                              setProjectSearchBusy(false);
                              setExternalValue('');
                              suppressNextProjectSearchRef.current = true;
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== ' ') return;
                              e.preventDefault();
                              e.stopPropagation();
                              const nextId = String(doc.id);
                              const nextLabel = String(doc.name || `Document ${nextId}`);
                              setSelectedProjectDocId(nextId);
                              setSelectedProjectDocLabel(nextLabel);
                              setProjectQuery('');
                              setProjectResults([]);
                              setProjectSearchBusy(false);
                              setExternalValue('');
                              suppressNextProjectSearchRef.current = true;
                            }}
                          >
                            <span>{doc.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="linkEditControls">
              <span className="hint">Launch in:</span>
              <div className="caretWrapper">
                <button
                  ref={caretRef}
                  type="button"
                  className="caretTextButton"
                  data-context-placement="bottom"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowLaunchMenu(s => !s); }}
                >
                  {selectedLaunchLabel}
                  <Icon icon={showLaunchMenu ? 'caret-up' : 'caret-down'} className="icon caretIcon small" />
                </button>
                {showLaunchMenu && (
                  <ContextMenu
                    items={launchItems}
                    triggerRef={caretRef}
                    placement="bottom"
                    menuClass="linkLaunchMenu"
                    onClose={() => setShowLaunchMenu(false)}
                  />
                )}
              </div>
              <div className="linkEditDoneAction">
                <button className="iconButton" aria-label="Done" onClick={saveEdit}>
                  <Icon icon={'check'} className="icon check" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Popover>
  );
}
